// Fundament2030 (I2) — Gewinn wird Gebäude (AUFTRAG K3 + Kevin-Entscheid
// 03.07. abends: Nordstern 700 Mio €, Darstellung als BAUABSCHNITTE à
// 1 Mio €). docs/INSTRUMENTE-KATALOG.md I2 + V2 Blaupausen-Ästhetik.
//
// Aufbau:
// - Kopfzeile "ABSCHNITT n / 700 · x % VERMAUERT · NORDSTERN 700 MIO".
// - Lageplan klein: 700 Abschnitts-Zellen (35×20-Raster), fertige gefüllt,
//   aktiver Abschnitt pulsiert.
// - Aktiver Abschnitt groß: echte JA/BWA-Ziegel (1 Ziegel = 10k JÜ,
//   Läuferverband, Jahres-Marken), Zielkontur = 1-Mio-Abschnitt,
//   Kran setzt dort (Hub mit trägem Pendel, --ease-heavy),
//   Baustellen-Laser auf aktueller Höhe (amber bei stale).
// - Klick = BWA-Drawer (echter Flow).
//
// Daten-Kontrakt: stale → Laser amber · missing → Zielkontur + Bodenplatte
// + "JA-WERTE FEHLEN".

import type { JahresSchicht } from "../../lib/instrumente";
import { formatEurCompact } from "../../lib/bwa-aggregate";

interface Props {
  schichten: JahresSchicht[];
  /** Nordstern aus fokus.ziele (2030). Fallback: Kevin-Entscheid 700 Mio. */
  zielEur: number | null;
  stale?: boolean;
  onDetail?: () => void;
}

const NORDSTERN_FALLBACK_EUR = 700_000_000; // Kevin 03.07.: Gesamt-Kontur
const ABSCHNITT_EUR = 1_000_000; // 1 Bauabschnitt = 1 Mio €
const ZIEGEL_JE_ABSCHNITT = 100; // à 10k

const W = 200;
const H = 268;
const BODEN_Y = 224;
const ZIEGEL_W = 17;
const ZIEGEL_H = 6.5;
const PRO_REIHE = 6;
const BAU_X = 50;

// Lageplan: 700 Zellen im 70×10-Raster unter dem Bau.
const PLAN_COLS = 70;
const PLAN_ROWS = 10;
const PLAN_X = 4;
const PLAN_Y = 240;
const PLAN_DX = 2.8;
const PLAN_DY = 2.7;

export default function Fundament2030({ schichten, zielEur, stale, onDetail }: Props) {
  const nordstern = zielEur != null && zielEur > 0 ? zielEur : NORDSTERN_FALLBACK_EUR;
  const abschnitteGesamt = Math.max(1, Math.round(nordstern / ABSCHNITT_EUR));
  const gesamtEur = schichten.reduce((s, x) => s + x.jueEur, 0);
  const fertigeAbschnitte = Math.min(abschnitteGesamt, Math.floor(gesamtEur / ABSCHNITT_EUR));
  const aktiverAbschnitt = Math.min(abschnitteGesamt, fertigeAbschnitte + 1);
  const imAbschnittEur = Math.max(0, gesamtEur - fertigeAbschnitte * ABSCHNITT_EUR);
  const vermauertPct = Math.round((imAbschnittEur / ABSCHNITT_EUR) * 100);
  const missing = schichten.length === 0;

  // Aktiver Abschnitt groß: Ziegel des LAUFENDEN Abschnitts (Rest-JÜ nach
  // fertigen Abschnitten), nach Jahres-Schichten aufgeteilt — die letzten
  // Schichten füllen den Abschnitt.
  let reihe = 0;
  const layer: { schicht: JahresSchicht; ziegel: number; startReihe: number }[] = [];
  {
    // Von unten: älteste zuerst, aber nur der Anteil im aktiven Abschnitt.
    let uebrig = fertigeAbschnitte * ABSCHNITT_EUR; // wird "weggeschnitten"
    for (const s of schichten) {
      let anteil = s.jueEur;
      if (uebrig > 0) {
        const schnitt = Math.min(uebrig, anteil);
        uebrig -= schnitt;
        anteil -= schnitt;
      }
      const ziegel = Math.round(anteil / 10_000);
      if (ziegel <= 0) continue;
      layer.push({ schicht: s, ziegel, startReihe: reihe });
      reihe += Math.ceil(ziegel / PRO_REIHE);
    }
  }
  const bauHoehe = Math.min(reihe, Math.ceil(ZIEGEL_JE_ABSCHNITT / PRO_REIHE)) * ZIEGEL_H;
  const zielHoehe = Math.ceil(ZIEGEL_JE_ABSCHNITT / PRO_REIHE) * ZIEGEL_H;

  return (
    <button
      type="button"
      onClick={onDetail}
      className="h-full w-full text-left rounded-2xl border border-leitstand-hairline bg-leitstand-panel p-3 hover:bg-white/[0.05] transition-colors"
      aria-label={`Fundament — Bauabschnitt ${aktiverAbschnitt} von ${abschnitteGesamt}, Klick öffnet BWA-Detail`}
    >
      <div className="label-tracked mb-0.5">Fundament 2030</div>
      <div className="num-mono text-[9px] text-leitstand-text mb-1">
        ABSCHNITT {aktiverAbschnitt} / {abschnitteGesamt} · {vermauertPct} % VERMAUERT ·
        NORDSTERN {formatEurCompact(nordstern).toUpperCase()}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" aria-hidden>
        <defs>
          <pattern id="blaupause-raster" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 H 0 V 10" fill="none" stroke="rgba(99,102,241,0.12)" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect x={0} y={0} width={W} height={240} fill="url(#blaupause-raster)" />

        {/* Maßkette links mit Höhenkoten (Abschnitts-Skala). */}
        <line x1={26} y1={BODEN_Y} x2={26} y2={BODEN_Y - zielHoehe - 14} stroke="rgba(245,241,232,0.3)" strokeWidth={0.75} />
        <Kote y={BODEN_Y} text={`▽ ±0,00 · ${formatEurCompact(fertigeAbschnitte * ABSCHNITT_EUR)}`} />
        {!missing && <Kote y={BODEN_Y - bauHoehe} text={`▽ heute · ${formatEurCompact(gesamtEur)}`} betont />}
        <Kote y={BODEN_Y - zielHoehe} text={`▽ Abschnitt ${aktiverAbschnitt} voll · ${formatEurCompact(aktiverAbschnitt * ABSCHNITT_EUR)}`} />

        {/* Bodenplatte. */}
        <rect x={BAU_X - 6} y={BODEN_Y} width={PRO_REIHE * ZIEGEL_W + 12} height={5} fill="rgba(245,241,232,0.25)" />

        {/* Zielkontur des aktiven 1-Mio-Abschnitts (gestrichelt). */}
        <rect
          x={BAU_X}
          y={BODEN_Y - zielHoehe}
          width={PRO_REIHE * ZIEGEL_W}
          height={zielHoehe}
          fill="none"
          stroke="rgba(245,241,232,0.35)"
          strokeWidth={0.9}
          strokeDasharray="4 3"
        />

        {missing && (
          <text x={BAU_X + (PRO_REIHE * ZIEGEL_W) / 2} y={BODEN_Y - zielHoehe / 2} textAnchor="middle" fontSize="7.5" fill="#a8a29a" fontFamily="JetBrains Mono, monospace">
            JA-WERTE FEHLEN
          </text>
        )}

        {/* Echte JA/BWA-Ziegel im aktiven Abschnitt (Läuferverband). */}
        {layer.map(({ schicht, ziegel, startReihe }) => {
          const bricks: JSX.Element[] = [];
          let gesetzt = 0;
          const reihen = Math.ceil(ziegel / PRO_REIHE);
          for (let r = 0; r < reihen; r++) {
            const globalReihe = startReihe + r;
            const y = BODEN_Y - (globalReihe + 1) * ZIEGEL_H;
            if (y < BODEN_Y - zielHoehe) break; // Abschnitt voll
            const versatz = globalReihe % 2 === 1 ? ZIEGEL_W / 2 : 0;
            for (let c = 0; c < PRO_REIHE && gesetzt < ziegel; c++, gesetzt++) {
              bricks.push(
                <rect
                  key={`${schicht.jahr}-${r}-${c}`}
                  x={BAU_X + c * ZIEGEL_W + versatz - (versatz > 0 && c === PRO_REIHE - 1 ? ZIEGEL_W / 2 : 0)}
                  width={versatz > 0 && (c === 0 || c === PRO_REIHE - 1) ? ZIEGEL_W / 2 : ZIEGEL_W - 1}
                  y={y}
                  height={ZIEGEL_H - 1}
                  fill={schicht.festgestellt ? "rgba(226,190,142,0.5)" : "rgba(226,190,142,0.24)"}
                  stroke="rgba(11,11,12,0.6)"
                  strokeWidth={0.5}
                  className={schicht.festgestellt ? undefined : "fundament-ziegel-neu"}
                />,
              );
            }
          }
          const labelY = BODEN_Y - (startReihe + reihen) * ZIEGEL_H + 4;
          return (
            <g key={schicht.jahr}>
              {bricks}
              <text x={BAU_X + PRO_REIHE * ZIEGEL_W + 6} y={labelY} fontSize="6" fill="#a8a29a" fontFamily="JetBrains Mono, monospace">
                {schicht.jahr} · {formatEurCompact(schicht.jueEur)} {schicht.festgestellt ? "✓" : "(vorl.)"}
              </text>
            </g>
          );
        })}

        {/* Baustellen-Laser auf aktueller Bauhöhe (Puls; amber bei stale). */}
        {!missing && (
          <line
            x1={BAU_X - 14}
            y1={BODEN_Y - bauHoehe}
            x2={BAU_X + PRO_REIHE * ZIEGEL_W + 4}
            y2={BODEN_Y - bauHoehe}
            stroke={stale ? "#f59e0b" : "#ef4444"}
            strokeWidth={1}
            className="fundament-laser"
          />
        )}

        {/* Kran entfernt (06.07., Sichtprüfung: las sich als kaputte Linien /
            "Galgen"). Der Laser + die wachsende Mauer tragen die Geschichte. */}

        {/* Lageplan: 700 Abschnitte klein — fertige gefüllt, aktiver pulsiert. */}
        <text x={PLAN_X} y={PLAN_Y - 4} fontSize="6" fill="#a8a29a" fontFamily="JetBrains Mono, monospace">
          LAGEPLAN · {fertigeAbschnitte}/{abschnitteGesamt} ABSCHNITTE FERTIG
        </text>
        {Array.from({ length: Math.min(abschnitteGesamt, PLAN_COLS * PLAN_ROWS) }).map((_, i) => {
          const col = i % PLAN_COLS;
          const row = Math.floor(i / PLAN_COLS);
          const fertig = i < fertigeAbschnitte;
          const aktiv = i === aktiverAbschnitt - 1;
          return (
            <rect
              key={i}
              x={PLAN_X + col * PLAN_DX}
              y={PLAN_Y + row * PLAN_DY}
              width={2.2}
              height={2.1}
              fill={fertig ? "#a3e635" : aktiv ? "#ef4444" : "rgba(245,241,232,0.1)"}
              opacity={fertig ? 0.85 : aktiv ? 1 : 0.5}
              className={aktiv ? "fundament-laser" : undefined}
            />
          );
        })}
      </svg>
    </button>
  );
}

function Kote({ y, text, betont }: { y: number; text: string; betont?: boolean }) {
  return (
    <g>
      <line x1={22} y1={y} x2={30} y2={y} stroke="rgba(245,241,232,0.4)" strokeWidth={0.75} />
      <text x={2} y={y - 2} fontSize="6" fill={betont ? "#f5f1e8" : "#a8a29a"} fontFamily="JetBrains Mono, monospace">
        {text}
      </text>
    </g>
  );
}
